import { mapGetters } from 'vuex'
import { CHANNEL_LABELS, CHANNEL_COLORS, CHANNEL_OPTIONS } from '@/utils/campaignOptions'
import { MONTHS, TIERS_COLORS, CATEGORY_COLORS, DONUT_CHART_CONFIG, VERTICAL_BAR_STACKED_CONFIG_IMPRESSIONS, VERTICAL_BAR_STACKED_CONFIG_CLICKS, VERTICAL_BAR_STACKED_CONFIG_VISITS, VERTICAL_BAR_STACKED_CONFIG_CTR, HORIZONTALL_BAR_STACKED_CONFIG } from '@/utils/dashboardChartsOptions'
import { MODULE_NAME, GETTERS } from '@/store/filters/constants'
import dashboardService from '@/api/dashboardService'
import customFormatters from '@/filters/customFormatters'
import moment from 'moment'
import authService, { USER_ROLES } from '@/api/authService'
import i18n from '@/i18n'

const indicatorOptions = [
  { id: 'IMPRESSIONS', name: i18n.t('IMPRESSIONS') },
  { id: 'CLICKS', name: i18n.t('CLICKS') },
  { id: 'CTR', name: i18n.t('CTR') },
  { id: 'VISITS', name: i18n.t('VISITS') }
]

const AREA_LABELS = {
  adverts: i18n.t('ADVERTS'),
  shops: i18n.t('CENTERS'),
  pois: i18n.t('COMPETITORS'),
  postal_codes: i18n.tc('ZIPCODE', 2)
}

export default {
  name: 'ChannelDashboard',
  mixins: [customFormatters],
  components: {
    CombinedChart: () => import(/* webpackChunkName: "CombinedChart" */'@/components/combined-chart/CombinedChart.vue'),
    PieChart: () => import(/* webpackChunkName: "PieChart" */'@/components/pie-chart/PieChart.vue'),
    TableWidget: () => import(/* webpackChunkName: "PieChart" */'@/components/table-widget/TableWidget.vue'),
    CampaignChannelTable: () => import(/* webpackChunkName: "CampaignChannelTable" */ '@/components/campaign-channel-table/CampaignChannelTable.vue'),
    MapWidget: () => import(/* webpackChunkName: "MapWidget" */'@/components/map-widget/MapWidget.vue'),
    HistogramChart: () => import(/* webpackChunkName: "HistogramChart" */'@/components/common/histogram-chart/HistogramChart.vue'),
    SimpleAreaChart: () => import(/* webpackChunkName: "SimpleAreaChart" */'@/components/common/simple-area-chart/SimpleAreaChart.vue'),
    DonutChart: () => import(/* webpackChunkName: "DonutChart" */'@/components/common/donut-chart/DonutChart'),
    BarChart: () => import(/* webpackChunkName: "BarChart" */'@/components/common/bar-chart/BarChart'),
    AnimatedNumber: () => import(/* webpackChunkName: "AnimatedNumber" */'@/components/common/animated-number/AnimatedNumber')
  },
  props: {
    showHeader: {
      type: Boolean,
      default: true
    }
  },
  data () {
    // const channelType = this.$route.params.type
    return {
      loading: true,
      authData: authService.authData,
      chartColors: ['#9C1A43', '#11a579', '#3969ac', '#f2b701', '#e73f74'],
      scrollPointReached: false,
      indicatorOptions: indicatorOptions,
      primaryIndicator: indicatorOptions[0].id,
      totals: null,
      charts: [
        {
          key: 'centers',
          title: i18n.t('CENTERS_WIDGET'),
          display: 'line-bar',
          data: { primary: [], secondary: [] },
          hidden: this.isDirector,
          headerOptions: [
            {
              label: i18n.tc('TOP_N', 5),
              value: { order: 'top', elements: 5 },
              selected: true
            }, {
              label: i18n.tc('BOTTOM_N', 5),
              value: { order: 'bottom', elements: 5 }
            }
          ],
          loading: true
        },
        {
          key: 'campaigns',
          title: i18n.t('CAMPAIGNS_WIDGET'),
          display: 'line-bar',
          data: { primary: [], secondary: [] },
          hidden: false,
          loading: true,
          barsOpacity: 0.4,
          secondaryIndicator: indicatorOptions[1].id
        },
        {
          key: 'campaigns_products',
          title: i18n.t('CAMPAIGNS_CHANNELS_WIDGET'),
          display: 'campaign-product-table',
          data: {
            meta: null,
            results: []
          },
          hidden: false,
          loading: true,
          auto: true,
          params: {
            page: 1,
            page_size: 10,
            ordering: null
          },
          downloadHandler: this.downloadCampaignsCSV.bind(this),
          downloadLoading: false
        }
      ],
      dynamicCharts: [],
      tiersCharts: [
        {
          key: 'investment-by-tier',
          title: i18n.t('BY_REGION'),
          charts: [
            {
              key: 'tier_global',
              title: i18n.t('INVESTMENT'),
              display: 'animated-number',
              data: null,
              loading: true,
              hidden: false,
              width: '100%',
              height: 'auto'
            },
            {
              key: 'tier_global_pie',
              type: 'echarts',
              title: '',
              display: 'donut-chart',
              data: DONUT_CHART_CONFIG,
              loading: true,
              hidden: true,
              width: '50%'
            },
            {
              key: 'tier_dates',
              type: 'echarts',
              title: '',
              display: 'horizontal-bar-chart',
              data: HORIZONTALL_BAR_STACKED_CONFIG,
              loading: true,
              hidden: true,
              height: 350,
              width: '50%'
            }
          ]
        },
        {
          key: 'impressions-clicks-sales-ctr',
          title: '',
          charts: [
            {
              key: 'tier_impressions',
              title: i18n.t('IMPRESSIONS'),
              value: '-',
              display: 'vertical-bar-chart',
              data: VERTICAL_BAR_STACKED_CONFIG_IMPRESSIONS,
              loading: true,
              hidden: true,
              width: '50%',
              height: 150,
              type: 'echarts'
            },
            {
              key: 'tier_clicks',
              title: 'Clicks',
              value: '-',
              display: 'vertical-bar-chart',
              data: VERTICAL_BAR_STACKED_CONFIG_CLICKS,
              type: 'echarts',
              loading: true,
              hidden: true,
              width: '50%',
              height: 150
            },
            {
              key: 'tier_visits',
              title: i18n.t('VISITS'),
              value: '-',
              display: 'vertical-bar-chart',
              data: VERTICAL_BAR_STACKED_CONFIG_VISITS,
              type: 'echarts',
              loading: true,
              hidden: true,
              width: '50%',
              height: 150
            },
            {
              key: 'tier_ctr',
              title: 'CTR %',
              display: 'vertical-bar-chart',
              value: '-',
              data: VERTICAL_BAR_STACKED_CONFIG_CTR,
              type: 'echarts',
              loading: true,
              hidden: true,
              width: '50%',
              height: 150
            }
          ]
        },
        // NOTE: Dynamic charts based on tiers data /dashboard/channels/stats/extended/tier/shops
        {
          key: 'tiers',
          title: '',
          noUI: true,
          charts: [
            {
              key: 'tier_shops',
              type: 'echarts',
              dynamic: true
            }
          ]
        }

      ],
      totalLoadingTiersChartsData: 0,
      totalClicks: 0,
      totalImpressions: 0
    }
  },
  computed: {
    ...mapGetters(MODULE_NAME, {
      filters: GETTERS.GET_ROUTE_FILTERS
    }),
    channelType () {
      return this.$route.params.type
    },
    channelLabel () {
      return CHANNEL_LABELS[this.channelType]
    },
    channelColor () {
      return CHANNEL_COLORS[this.channelType]
    },
    secondaryIndicatorOptions () {
      return this.indicatorOptions.filter(opt => opt.id !== this.primaryIndicator)
    },
    primaryIndicatorLabel () {
      const ind = indicatorOptions.find(opt => opt.id === this.primaryIndicator)
      return ind.name
    },
    totalCampaignCount () {
      return this.totals && this.totals.count
    },
    totalBudget () {
      return this.totals && this.totals.budget
    },
    labelBlocks () {
      const chart = this.charts.find(c => c.display === 'pie')
      if (!chart) {
        return []
      }
      const blocks = {}
      for (const group of chart.data.primary) {
        for (const value of group.values) {
          blocks[value.key] = blocks[value.key] || {
            key: value.key,
            label: AREA_LABELS[value.key],
            primary: 0,
            secondary: 0
          }
          blocks[value.key].primary += value.value
        }
      }
      for (const group of chart.data.secondary) {
        for (const value of group.values) {
          blocks[value.key] = blocks[value.key] || {
            key: value.key,
            label: AREA_LABELS[value.key],
            primary: 0,
            secondary: 0
          }
          blocks[value.key].secondary += value.value
        }
      }
      return Object.values(blocks).map((b, i) => ({ ...b, color: this.chartColors[i] }))
    },
    startDateFilter () {
      return this.filters.find(f => f.key === 'start_date')
    },
    finishDateFilter () {
      return this.filters.find(f => f.key === 'finish_date')
    },
    useDayLabelInCharts () {
      const start = this.startDateFilter.value
      const finish = this.finishDateFilter.value
      return moment(finish).diff(start, 'month') <= 4
    },
    isDirector () {
      return this.authData.user.role === USER_ROLES.DIRECTOR
    },
    isYouTube () {
      return this.channelType === CHANNEL_OPTIONS.YOUTUBE
    },
    isFacebook () {
      return this.channelType === CHANNEL_OPTIONS.FACEBOOK
    },
    isInstagram () {
      return this.channelType === CHANNEL_OPTIONS.INSTAGRAM
    },
    isLoadingTierChartsData () {
      return this.totalLoadingTiersChartsData > 0
    },
    updateCtr () {
      this.tiersCharts
        .find(c => c.key === 'impressions-clicks-sales-ctr').charts
        .find(c => c.key === 'tier_ctr').value = `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(this.totalClicks / this.totalImpressions * 100)}`
    }
  },
  watch: {
    totalClicks () {
      if (this.totalClicks > 0 && this.totalImpressions > 0) {
        this.updateCtr && this.updateCtr()
      }
    },
    totalImpressions () {
      if (this.totalClicks > 0 && this.totalImpressions > 0) {
        this.updateCtr && this.updateCtr()
      }
    },
    filters () {
      if (!this.loading) {
        this.fetchTotals()
        // To reset pagination on global filters changes
        this.charts.find(c => c.key === 'campaigns_products').params.page = 1
        this.fetchAllChartData()
      }
    },
    isDirector: {
      immediate: true,
      handler () {
        this.charts[0].hidden = this.isDirector
      }
    }
  },
  async mounted () {
    window.scrollTo(0, 0)
    window.addEventListener('scroll', this.setScrollPoint, false)
    await this.fetchTotals()
    await this.fetchAllChartData()
    this.loading = false
    this.hideCharts()
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.setScrollPoint, false)
  },
  methods: {
    hideCharts () {
      this.charts.forEach(chart => {
        // if (chart.key === 'areatypes' || chart.key === 'campaigns_products') {
        //   chart.hidden = this.isYouTube
        // }
        // if (chart.key === 'video_completion') {
        //   chart.hidden = !this.isYouTube
        // }
      })
    },
    async fetchTotals () {
      try {
        if (this.channelType !== undefined) {
          const totals = await dashboardService.getChannelTypeStats({ type: this.channelType }, this.filters)
          this.totals = totals
        }
      } catch (err) {
        // eslint-disable-next-line
        console.error('[channel-dashboard.js] Error fetching totals', err)
      }
    },
    setScrollPoint () {
      this.scrollPointReached = window.pageYOffset > 230
    },
    getChartMargin (c) {
      // const right = c.secondaryIndicator ? 60 : 20
      return { top: 20, right: 65, bottom: 20, left: 65 }
    },
    getPrimaryLabel (c) {
      const label = this.primaryIndicatorLabel
      return c.secondaryIndicator ? `${label} (${i18n.t('LINES')})` : i18n.t('NUM_OF_N', { n: label })
    },
    getSecondaryLabel (c) {
      const ind = indicatorOptions.find(opt => opt.id === c.secondaryIndicator)
      return ind && `${ind.name} (${i18n.t('BARS')})`
    },
    getSecondaryPieLabel (c) {
      const ind = indicatorOptions.find(opt => opt.id === c.secondaryIndicator)
      return ind && ind.name
    },
    checkIndicatorOverlap () {
      this.charts.forEach(c => {
        if (c.secondaryIndicator === this.primaryIndicator) {
          c.secondaryIndicator = this.secondaryIndicatorOptions[0].id
        }
      })
    },
    onPrimaryIndicatorSelected (opt) {
      this.primaryIndicator = opt.id
      this.checkIndicatorOverlap()
      this.fetchAllChartData()
    },
    onSecondaryIndicatorSelected (opt, chart) {
      chart.secondaryIndicator = opt.id
      this.fetchChartData(chart)
    },
    fetchAllChartData () {
      this.charts.forEach(c => {
        if (c.hidden) {
          c.data = null
        } else {
          this.fetchChartData(c)
        }
      })

      this.totalLoadingTiersChartsData = this.tiersCharts.reduce((acc, curr) => acc + curr.charts.length, 0) - 1
      this.tiersCharts.forEach(t => {
        t.charts.forEach(c => {
          this.fetchChartData(c)
        })
      })
    },
    onChartPagination (page, chart) {
      chart.params.page = page
      return this.fetchChartData(chart)
    },
    onChartSort (sortBy, chart) {
      chart.params.ordering = sortBy
      return this.fetchChartData(chart)
    },
    async fetchChartData (chart) {
      chart.loading = true
      let params = {
        type: this.channelType,
        primary_metric: this.primaryIndicator,
        secondary_metric: chart.secondaryIndicator
      }
      if (chart.params) {
        params = { ...params, ...chart.params }
      }
      const mapping = {
        centers: 'getStatsByCenters',
        campaigns: 'getStatsByCampaigns',
        areatypes: 'getStatsByAreaType',
        products: 'getStatsByProducts',
        campaigns_products: 'getCampaignsChannels',
        video_completion: 'getStatsYT',
        video_completion_playback: 'getStatsYT',
        tier_global: 'getStatsByTierGlobal',
        tier_global_pie: 'getStatsByTierGlobal',
        tier_impressions: 'getStatsByTierImpressions',
        tier_clicks: 'getStatsByTierClicks',
        tier_visits: 'getStatsByTierVisits',
        tier_ctr: 'getStatsByTierCtr',
        tier_shops: 'getStatsByTierShops',
        tier_dates: 'getStatsByTierDates'
      }

      if (chart.key === 'centers') {
        const _option = chart.headerOptions
          .find(o => o.selected)
        params = { ...params, ..._option.value }
      }

      const method = mapping[chart.key]

      try {
        if (chart.type === 'echarts') {
          params = { ...params, page_size: 100000 }
        }
        const data = await dashboardService[method](params, this.filters)

        if (chart.type === 'echarts') {
          this.totalLoadingTiersChartsData--
        }

        if (data.results && data) {
          // Order by terr_unit desc
          data.results = data.results.sort((a, b) => b.terr_unit - a.terr_unit)
        }

        // NOTE: data.results provides from campaigns_products
        const transformed = this.transformChartData(chart, data.results || data)

        if (chart.type && chart.type === 'echarts') {
          this.populateEchartsData(chart, data, transformed)
        } else {
          chart.data = transformed
          // Table pagination
          chart.data.meta = data.meta
        }
      } catch (err) {
        // eslint-disable-next-line
        console.error('[channel-dashboard.js] Error fetching data for chart', err)
      }
      chart.loading = false
    },
    transformChartData (chart, data) {
      const mapping = {
        centers: 'transformCentersData',
        campaigns: 'transformCampaignsData',
        campaigns_products: 'transformCampaignsProductsData',
        areatypes: 'transformAreaTypeData',
        products: 'transformProductsData',
        video_completion: 'transformVideoHistogram',
        video_completion_playback: 'transformVideoArea',
        tier_global: 'transformTierGlobalData',
        tier_global_pie: 'transformTierGlobalPieData',
        tier_impressions: 'transformTierImpressionsData',
        tier_clicks: 'transformTierClicksData',
        tier_visits: 'transformTierVisitsData',
        tier_ctr: 'transformTierCtrData',
        tier_shops: 'transformTierShopsData',
        tier_dates: 'transformTierDatesData'
      }
      const transform = this[mapping[chart.key]] || (d => d)

      return transform(data, chart)
    },
    transformCampaignsProductsData (data, chart) {
      return {
        results: data
      }
    },
    groupDataByTerrUnit (data) {
      const groupedData = {}

      if (Array.isArray(data)) {
        for (const item of data) {
          const terrUnit = item.terr_unit

          if (!groupedData[terrUnit]) {
            groupedData[terrUnit] = []
          }

          groupedData[terrUnit].push(item)
        }
      }

      return groupedData
    },
    groupDataByDate (data) {
      const groupedData = {}

      if (Array.isArray(data)) {
        for (const item of data) {
          // NOTE: Create a date string YYYYMM to group data by date
          const date = `${item.year}${item.month.toString().padStart(2, '0')}`

          if (!groupedData[date]) {
            groupedData[date] = []
          }
          groupedData[date].push(item)
        }
        // Create a dictionary to hold data for each terr_unit
        const terrUnitsData = {}
        // Max tier lenght
        const maxTier = Math.max(...data.map(d => d.terr_unit))
        // Check if any Tier has no data, if no, add it with 0 budget
        for (const month in groupedData) {
          for (let i = 1; i <= maxTier; i++) {
            // Check if exists data for terr_unit i
            const exists = groupedData[month].find(d => d.terr_unit === i.toString())
            // If not exists, add it with 0 budget
            if (!exists) {
              groupedData[month].push({ terr_unit: i.toString(), budget: 0, month: parseInt(month) })
            }
          }
        }
        // Iterate over each month in the data
        for (const month in groupedData) {
          // Iterate over each entry in the month
          groupedData[month].forEach(entry => {
            // If this terr_unit doesn't have an entry in terrUnitsData, create one
            if (!terrUnitsData[entry.terr_unit]) {
              terrUnitsData[entry.terr_unit] = {
                name: `Tier ${entry.terr_unit}`,
                type: 'bar',
                color: TIERS_COLORS[entry.terr_unit],
                stack: 'total',
                label: {
                  show: false
                },
                emphasis: {
                  focus: 'series'
                },
                data: [],
                cursor: 'default'
              }
            }

            // Push the budget onto the data array for this terr_unit
            terrUnitsData[entry.terr_unit].data.push(entry.budget)
          })
        }
        // Extract the values to get an array of the restructured terr_unit objects
        return Object.values(terrUnitsData)
      }

      return null
    },
    createDinamyCharts (transformed) {
      const dynamicCharts = []
      this.dynamicCharts = dynamicCharts

      for (const key in transformed) {
        const budgetData = []
        const clicksData = []
        const impressionsData = []
        const categories = transformed[key].map(d => d.name.replace('Tecnomat', ''))

        for (const item of transformed[key]) {
          budgetData.push(item.budget)
          clicksData.push(item.clicks)
          impressionsData.push(item.impressions)
        }

        const dynamicChart = {
          key: key,
          title: '',
          display: 'bar-chart',
          hidden: false,
          data: {
            ...HORIZONTALL_BAR_STACKED_CONFIG,
            xAxis: {
              type: 'category',
              data: categories,
              axisLabel: {
                show: true,
                interval: 0,
                rotate: budgetData.length > 5 ? 45 : 0,
                formatter: function (value, index) {
                  const label = value
                  const splittedLabel = label.split(' ')
                  return budgetData.length > 5 && splittedLabel.length > 2 ? `${splittedLabel[1]}\n${splittedLabel[2]}` : label
                }
              }
            },
            title: { text: `Tier ${key}` },
            series: [
              {
                name: i18n.t('BUDGET'),
                type: 'bar',
                label: {
                  show: false,
                  position: 'top'
                },
                color: TIERS_COLORS[transformed[key][0].terr_unit],
                data: budgetData,
                cursor: 'default'
              },
              {
                name: 'Clicks',
                type: 'bar',
                label: {
                  show: false,
                  position: 'top'
                },
                color: TIERS_COLORS[transformed[key][0].terr_unit] + CATEGORY_COLORS[1],
                data: clicksData,
                cursor: 'default'
              },
              {
                name: i18n.t('IMPRESSIONS'),
                type: 'bar',
                label: {
                  show: false,
                  position: 'top'
                },
                color: TIERS_COLORS[transformed[key][0].terr_unit] + CATEGORY_COLORS[2],
                data: impressionsData,
                cursor: 'default'
              }
            ]
          },
          width: '100%',
          height: 250
        }
        dynamicCharts.push(dynamicChart)
      }
      this.dynamicCharts = dynamicCharts
    },
    transformTierShopsData (data) {
      return this.groupDataByTerrUnit(data)
    },
    transformTierDatesData (data) {
      return this.groupDataByDate(data)
    },
    transformTierImpressionsData (data) {
      const series = Array.isArray(data) ? data.map(d => {
        return {
          tier: d.terr_unit,
          itemStyle: { color: TIERS_COLORS[d.terr_unit] },
          value: Number(d.impressions),
          rawValue: d.impressions
        }
      }) : []

      return series
    },
    transformTierClicksData (data) {
      const series = Array.isArray(data) ? data.map(d => {
        return {
          itemStyle: { color: TIERS_COLORS[d.terr_unit] },
          value: d.clicks,
          rawValue: d.clicks
        }
      }) : []

      return series
    },
    transformTierVisitsData (data) {
      const series = Array.isArray(data) ? data.map(d => {
        return {
          itemStyle: { color: TIERS_COLORS[d.terr_unit] },
          value: d.visits,
          rawValue: d.visits
        }
      }) : []

      return series
    },
    transformTierCtrData (data) {
      const series = Array.isArray(data) ? data.map(d => {
        return {
          itemStyle: { color: TIERS_COLORS[d.terr_unit] },
          value: d.ctr,
          rawValue: d.ctr
        }
      }) : []

      return series
    },
    transformTierGlobalData (data) {
      return {
        value: Array.isArray(data) ? new Intl.NumberFormat('it-IT', {
          style: 'currency',
          currency: 'EUR'
        }).format(data.reduce((acc, curr) => acc + curr.budget, 0)) : 0
      }
    },
    transformTierGlobalPieData (data) {
      let series = []
      if (Array.isArray(data)) {
        // Order data by terr_unit asc
        series = data.sort((a, b) => a.terr_unit - b.terr_unit).map(d => {
          return {
            name: `Tier ${d.terr_unit}`,
            value: d.budget,
            itemStyle: { color: TIERS_COLORS[d.terr_unit] }
          }
        })
      }

      return series
    },
    transformCentersData (data) {
      return {
        primary: {
          type: 'line',
          data: data.map(d => {
            return {
              date: new Date(d.date),
              values: d.data.map(v => ({ key: v.shop, value: v.primary_metric })).filter(v => v.key)
            }
          })
        }
      }
    },
    transformCampaignsData (data, chart) {
      return {
        primary: {
          type: 'line',
          data: data.map(d => {
            return {
              date: new Date(d.date),
              values: [{ key: this.getPrimaryLabel(chart), value: d.primary_metric }]
            }
          })
        },
        secondary: {
          type: 'bar',
          data: data.map(d => {
            return {
              date: new Date(d.date),
              values: [{ key: this.getSecondaryLabel(chart), value: d.secondary_metric }]
            }
          })
        }
      }
    },
    transformAreaTypeData (data) {
      const primary = []
      const secondary = []
      let ctr = 0
      for (const group of data) {
        const primaryEl = { date: new Date(group.date), values: [] }
        const secondaryEl = { date: new Date(group.date), values: [] }
        for (const value of group.data) {
          for (const key in value) {
            primaryEl.values.push({ key, value: value[key].primary_metric })
            secondaryEl.values.push({ key, value: value[key].secondary_metric })
            ctr = value[key].global_ctr_metric
          }
        }
        primary.push(primaryEl)
        secondary.push(secondaryEl)
      }
      return { primary, secondary, ctr }
    },
    transformProductsData (data) {
      return {
        primary: data
      }
    },
    transformVideoHistogram (data) {
      const info = data[0] || {}
      return [
        {
          name: i18n.t('PERCENT_OF_VIDEO', { n: 25 }),
          values: [info.quartile_1 || 0]
        },
        {
          name: i18n.t('PERCENT_OF_VIDEO', { n: 50 }),
          values: [info.quartile_2 || 0]
        },
        {
          name: i18n.t('PERCENT_OF_VIDEO', { n: 75 }),
          values: [info.quartile_3 || 0]
        },
        {
          name: i18n.t('PERCENT_OF_VIDEO', { n: 100 }),
          values: [info.quartile_4 || 0]
        }
      ]
    },
    transformVideoArea (data) {
      const info = data[0] || {}
      return [
        {
          name: 25,
          value: info.quartile_1 || 0,
          percentage: info.quartile_1_percent || 0
        },
        {
          name: 50,
          value: info.quartile_2 || 0,
          percentage: info.quartile_2_percent || 0
        },
        {
          name: 75,
          value: info.quartile_3 || 0,
          percentage: info.quartile_3_percent || 0
        },
        {
          name: 100,
          value: info.quartile_4 || 0,
          percentage: info.quartile_4_percent || 0
        }
      ]
    },
    populateEchartsData (chart, data, transformed) {
      if (!data.results) {
        chart.hidden = true
        this.dynamicCharts = []
        return
      }

      // NOTE: Create dynamic charts based on data terr_unit
      if (chart.dynamic) {
        this.createDinamyCharts(transformed)
        return
      }

      // Common charts
      if (chart.data.series && Array.isArray(chart.data.series)) {
        chart.data.series[0].data = transformed
        chart.hidden = false
      }

      // Vertical bar stacked
      if (chart.data.yAxis && chart.data.yAxis.type === 'category' && data.results) {
        chart.data.yAxis.data = data.results.map(d => `Tier ${d.terr_unit}`)
      }

      // Horizontal bar stacked
      if (chart.data.yAxis && chart.data.yAxis.type === 'value' && data.results) {
        chart.data.xAxis.data = [...new Set(data.results.map(d => `${MONTHS[d.month - 1]}`))]
        chart.data.series = transformed
        chart.hidden = true
        // Need to refresh chart
        setTimeout(() => {
          chart.hidden = false
        }, 20)
      }

      if (chart.value && chart.key !== 'tier_ctr') {
        let total = transformed.reduce((acc, curr) => acc + curr.rawValue, 0)

        // Need to calculate total ctr% clicks/impressions * 100
        if (chart.key === 'tier_clicks') {
          this.totalClicks = total
        }

        if (chart.key === 'tier_impressions') {
          this.totalImpressions = total
        }

        // NOTE: CTR median defined into watchers
        // Need to calculate total ctr% clicks/impressions * 100
        if (chart.key === 'tier_clicks') {
          this.totalClicks = total
        }

        if (chart.key === 'tier_impressions') {
          this.totalImpressions = total
        }

        chart.value = `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(total)}`
      }
    },
    onSelectHeaderOption (chart, option) {
      chart.headerOptions
        .forEach(o => this.$set(o, 'selected', false))
      const _option = chart.headerOptions
        .find(o => option.value === o.value)
      _option.selected = true
      this._onSelectedHeaderOptionChart(_option.value)
    },
    async _onSelectedHeaderOptionChart (value) {
      const chart = this.charts[0]
      chart.loading = true
      const params = {
        type: this.channelType,
        primary_metric: this.primaryIndicator,
        secondary_metric: chart.secondaryIndicator,
        ...value
      }
      try {
        const data = await dashboardService.getStatsByCenters(params, this.filters)
        const transformed = this.transformChartData(chart, data)
        chart.data = transformed
      } catch (err) {
        // eslint-disable-next-line
        console.error('[channel-dashboard.js] Error fetching data for chart', err)
      }
      chart.loading = false
    },
    async downloadCampaignsCSV (chart) {
      this.$set(chart, 'downloadLoading', true)
      try {
        // NOTE: Add custom dates to prevent global changes
        const startDateObject = new Date(this.filters.find(f => f.key === 'start_date').value)
        const finishDateObject = new Date(this.filters.find(f => f.key === 'finish_date').value)
        const startDate = startDateObject.toISOString().split('T')[0]
        const finishDate = finishDateObject.toISOString().split('T')[0]
        const channelType = this.filters.find(f => f.key === 'campaign_channel').value
        const shopsId = this.filters.find(f => f.key === 'shop_id').value

        const params = {
          ...chart.params,
          page: 1,
          page_size: 100000,
          type: channelType,
          download: 'xlsx',
          start_date: startDate,
          finish_date: finishDate,
          campaign__shops__id__in: shopsId
        }

        await dashboardService.getCampaignsCSV(params, this.filters)
      } catch (err) {
        // eslint-disable-next-line
        console.error('[channel-dashboard.js] Error generating campaigns CSV', err)
      } finally {
        this.$set(chart, 'downloadLoading', false)
      }
    }
  }
}
